import { useContext } from "react";
import { getAuthenticatedUserClient } from "../../../utils/getAuthenticatedUser";
import { V3ModalAddContentResult, V3ModalAddContentResultProps } from "./AddContentResult";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { CookieContext } from "../../../context/cookieContext";
import { ModifyCollectionContent } from "@authory-packages/core";
import { getAddRemoveItemFromCollectionMutation } from "../../../utils/mutation-utils/collections/AddRemoveItemFromCollection";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { getItemQuerie, Queries } from "../../../types/queries";
import { getArticle } from "../../../api/articles";

export const AddContentResultContainer = ({
    onCloseHandler,
    items
}: Omit<V3ModalAddContentResultProps, "addOrRemoveItemManuallyFromCollection" | "collections">) => {
    const router = useRouter();
    const token = useContext(CookieContext);
    const { authenticatedUser } = getAuthenticatedUserClient(token, router, true);
    const queryClient = useQueryClient();

    const itemsQueries = useQueries({
        queries: items.map((item) => ({
            queryKey: getItemQuerie(token, item.canonicalSlug),
            queryFn: () => getArticle({ token, authorSlug: authenticatedUser.data?.slug!, itemSlug: item.canonicalSlug })
        }))
    });

    // When nothing was added (e.g. the user skipped every item) there are no queries to wait on,
    // so we're immediately "ready" and fall through to the empty state instead of an infinite loader.
    const atLeastOneLoadedSuccessfully = !items.length || itemsQueries.some((q) => q.isSuccess);

    // Add item to collection mutation
    const addItemToCollectionMutation = getAddRemoveItemFromCollectionMutation(queryClient);

    const addOrRemoveItemManuallyFromCollection = async (add: boolean, articleSlug: string, collectionSlug: string) => {
        if (!token || !authenticatedUser.data?.slug) return;

        const payload: ModifyCollectionContent = {
            articleIds: [articleSlug]
        };

        // Add item to collection
        await addItemToCollectionMutation.mutateAsync({
            token,
            userSlug: authenticatedUser.data?.slug as string,
            collectionSlug,
            payload,
            add: add,
            useContentDetaisOptimisticUpdate: true
        });

        queryClient.invalidateQueries({ queryKey: [Queries.AuthorItem] });
        queryClient.invalidateQueries({ queryKey: [Queries.User] });

        if (add) toast.success("Item has been added!");
        else toast.success("Item has been removed!");
    };

    const items2 = itemsQueries.map((it) => it.data?.article).filter((it) => !!it);

    return (
        <V3ModalAddContentResult
            onCloseHandler={onCloseHandler}
            items={items2}
            isLoading={atLeastOneLoadedSuccessfully}
            collections={authenticatedUser.data?.collections || []}
            addOrRemoveItemManuallyFromCollection={addOrRemoveItemManuallyFromCollection}
        />
    );
};
